import { INITIAL_REGISTER_FORM_VALUES } from "@repo/constants/account";
import { useSnackbarContext } from "@repo/contexts/SnackbarContext";
import { validatePasswordsMatch } from "@repo/lib/helpers";
import { useFormValidation } from "@repo/lib/hooks/common";
import {
  useUserAttributes,
  useUserSignupEvent,
} from "@repo/lib/hooks/webengage";
import { getUserSessionServer, validation } from "@repo/lib/utils";
import { authenticate } from "@repo/server-actions/account";
import { addToWishlist } from "@repo/server-actions/wishlist";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useTransition } from "react";

const useRegister = (props) => {
  const { variant = "primary", handleCheckoutSignIn } = props || {};
  const [isPending, startTransition] = useTransition();

  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("Common");
  const { setSnackbar } = useSnackbarContext();

  const searchParams = useSearchParams();
  const pageRedirectUrl = searchParams.get("redirect"); // redirection is to handle redirection to wishlist page
  const productId = searchParams.get("productId");
  const { formValues, formErrors, setFormErrors, handleValidation } =
    useFormValidation(INITIAL_REGISTER_FORM_VALUES);
  const { handleTrackUserSignupEvent } = useUserSignupEvent();
  const { handleSetUserAttributes } = useUserAttributes();

  const submitForm = useCallback(async () => {
    const response = await authenticate(formValues, locale, true);

    if (response?.success) {
      const session = await getUserSessionServer();
      handleTrackUserSignupEvent(session?.user ?? response);
      await handleSetUserAttributes(session);
      if (variant === "secondary") {
        handleCheckoutSignIn();
        router.refresh();
      }
      if (pageRedirectUrl) {
        try {
          const wishlistRes = await addToWishlist(productId, locale);
          setSnackbar({
            open: true,
            message: wishlistRes?.message,
            type: wishlistRes?.success ? "success" : "error",
          });
          router.push(pageRedirectUrl);
        } catch (error) {
          console.error("Error adding to wishlist:", error);
        }
      } else {
        if (variant === "secondary") {
          router.refresh();
        } else {
          window.location.assign("/account");
        }
      }
    }
    setSnackbar({
      open: true,
      message: response?.customMessage
        ? t(response?.message)
        : response?.message,
      type: response?.success ? "success" : "error",
    });
  }, [
    formValues,
    locale,
    setSnackbar,
    handleTrackUserSignupEvent,
    router,
    pageRedirectUrl,
    productId,
    handleSetUserAttributes,
    handleCheckoutSignIn,
    variant,
    t,
  ]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const validationErrors = validation(formValues, t);
      const passwordMatchErrors = validatePasswordsMatch(formValues, t);

      const errors = { ...validationErrors, ...passwordMatchErrors };

      if (Object.keys(errors).length === 0) {
        startTransition(() => submitForm());
      } else {
        setFormErrors(errors);
      }
    },
    [formValues, setFormErrors, submitForm, t]
  );

  return { handleSubmit, handleValidation, formErrors, isPending, formValues };
};

export default useRegister;
